<template>
  <div class="viva-form-page">
    <!-- Header -->
    <div class="viva-page-header">
      <div class="viva-header-content">
        <img src="/viva/viva_logo.svg" alt="Viva" class="viva-logo" />
      </div>
      <ProfileDropdown
        :student-name="instructorName"
        current-portal="instructor"
        :disable-portal-link="!isVivaAdmin"
      />
    </div>

    <!-- Page Title Section -->
    <div class="instructor-info">
      <h1 class="viva-page-title">{{ isNewCourse ? 'CREATE' : 'EDIT' }} COURSE</h1>
    </div>

    <!-- Form Container -->
    <div class="viva-form-container">
      <!-- Loading state -->
      <div v-if="loading" class="viva-form-section viva-form-section-green">
        <p>Loading course...</p>
      </div>

      <!-- Form -->
      <div v-else class="viva-form-section viva-form-section-green">
        <!-- Course Title -->
        <div class="form-group-inline">
          <label class="form-label-inline">
            COURSE TITLE:
          </label>
          <div class="form-input-wrapper">
            <input
              type="text"
              class="form-input"
              :class="{ 'error': errors.title }"
              v-model="course.title"
              placeholder="Enter course title"
            />
            <span class="required-asterisk">*</span>
            <div v-if="errors.title" class="error-message">{{ errors.title }}</div>
          </div>
        </div>

        <!-- Term -->
        <div class="form-group-inline">
          <label class="form-label-inline">
            TERM:
          </label>
          <div class="form-input-wrapper">
            <select
              class="form-select"
              v-model="course.term"
            >
              <option value="">Select term...</option>
              <option value="Academic Year">Academic Year</option>
              <option value="Spring">Spring</option>
              <option value="Summer">Summer</option>
              <option value="Fall">Fall</option>
              <option value="Winter">Winter</option>
              <option value="January">January</option>
              <option value="February">February</option>
              <option value="March">March</option>
              <option value="April">April</option>
              <option value="May">May</option>
              <option value="June">June</option>
              <option value="July">July</option>
              <option value="August">August</option>
              <option value="September">September</option>
              <option value="October">October</option>
              <option value="November">November</option>
              <option value="December">December</option>
            </select>
          </div>
        </div>

        <!-- Year -->
        <div class="form-group-inline">
          <label class="form-label-inline">
            YEAR:
          </label>
          <div class="form-input-wrapper">
            <select
              class="form-select"
              v-model="course.year"
            >
              <option value="">Select year...</option>
              <option v-for="year in yearOptions" :key="year" :value="year">{{ year }}</option>
            </select>
          </div>
        </div>

        <!-- Students Section (edit mode only) -->
        <div v-if="!isNewCourse" class="form-group-inline">
          <label class="form-label-inline">
            STUDENTS:
          </label>
          <div class="form-input-wrapper">
            <div class="student-roster">
              <div
                v-for="student in students"
                :key="student.email"
                class="student-roster-row"
              >
                <span class="student-roster-name">{{ studentDisplayName(student) }}</span>
                <span class="student-roster-email" v-if="student.first_name || student.last_name">{{ student.email }}</span>
                <button
                  type="button"
                  class="remove-btn"
                  @click="removeStudent(student)"
                  title="Remove student from course"
                >&times;</button>
              </div>
              <span v-if="students.length === 0" class="co-teacher-empty">
                No students enrolled.
              </span>
            </div>
          </div>
        </div>

        <!-- Co-Teachers Section (edit mode only) -->
        <div v-if="!isNewCourse" class="form-group-inline">
          <label class="form-label-inline">
            CO-TEACHER(S):
          </label>
          <div class="form-input-wrapper">
            <div class="co-teacher-tags">
              <span
                v-for="teacher in coTeachers"
                :key="teacher.id || teacher.email"
                class="co-teacher-tag"
              >
                <span class="co-teacher-tag-name">{{ formatName(teacher.name || teacher.email) }}</span>
                <span class="co-teacher-tag-email" v-if="teacher.name">{{ teacher.email }}</span>
              </span>
              <span v-if="coTeachers.length === 0" class="co-teacher-empty">
                No co-teachers added yet.
              </span>
              <button
                type="button"
                class="viva-btn viva-btn-outline viva-btn-small"
                @click="showCoTeacherModal = true"
              >
                + ADD
              </button>
            </div>
          </div>
        </div>

        <!-- Button Group -->
        <div class="form-button-group">
          <button class="viva-btn viva-btn-orange viva-btn-small" @click="cancel" :disabled="saving">
            CANCEL
          </button>
          <button class="viva-btn viva-btn-green viva-btn-small" @click="save" :disabled="saving">
            {{ saving ? 'SAVING...' : 'SAVE' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Add Co-Teacher Modal -->
    <div v-if="showCoTeacherModal" class="viva-modal-backdrop" @click.self="closeCoTeacherModal">
      <div class="viva-modal-content">
        <div class="viva-modal-header">
          <h2>Add a Co-Teacher</h2>
        </div>
        <div class="viva-modal-body">
          <div v-if="coTeacherError" class="co-teacher-error">{{ coTeacherError }}</div>
          <div v-if="coTeacherSuccess" class="co-teacher-success">{{ coTeacherSuccess }}</div>
          <div class="form-group">
            <label class="form-label">FIRST NAME:</label>
            <input
              type="text"
              class="form-input"
              v-model="newCoTeacher.first_name"
              placeholder="Enter first name"
            />
          </div>
          <div class="form-group">
            <label class="form-label">LAST NAME:</label>
            <input
              type="text"
              class="form-input"
              v-model="newCoTeacher.last_name"
              placeholder="Enter last name"
            />
          </div>
          <div class="form-group">
            <label class="form-label">EMAIL:</label>
            <input
              type="email"
              class="form-input"
              v-model="newCoTeacher.email"
              placeholder="Enter email address"
            />
          </div>
        </div>
        <div class="viva-modal-footer">
          <button class="viva-btn viva-btn-gray viva-btn-small" @click="closeCoTeacherModal">
            CANCEL
          </button>
          <button class="viva-btn viva-btn-green viva-btn-small" @click="addCoTeacher" :disabled="addingCoTeacher">
            {{ addingCoTeacher ? 'ADDING...' : 'ADD' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Back Link -->
    <div class="back-section">
      <a @click="goBack" class="back-link" style="cursor: pointer;">
        <span>←</span> {{ backLinkText }}
      </a>
    </div>
  </div>
</template>

<script>
import apiClient from '../utils/apiClient.js';
import { handleApiError } from '../utils/errorHandler.js';
import ProfileDropdown from '../components/ProfileDropdown.vue';
import { getStatusData } from '../utils/statusCache.js';

export default {
  name: 'CourseForm',
  components: { ProfileDropdown },

  data() {
    return {
      course: {
        id: null,
        title: '',
        term: '',
        year: ''
      },
      errors: {},
      isNewCourse: true,
      loading: true,
      instructorName: '',
      isVivaAdmin: false,
      saving: false,
      // Students
      students: [],
      // Co-teacher state
      coTeachers: [],
      showCoTeacherModal: false,
      addingCoTeacher: false,
      coTeacherError: '',
      coTeacherSuccess: '',
      newCoTeacher: { first_name: '', last_name: '', email: '' },
      // Concurrency
      originalUpdatedAt: null,
    };
  },

  computed: {
    backLinkText() {
      return this.$route.query.return_to === 'assignments'
        ? 'Back to Assignments'
        : 'Back to All Courses';
    },

    yearOptions() {
      const currentYear = new Date().getFullYear();
      const years = [];
      // Previous year through 2 years from now
      for (let year = currentYear - 1; year <= currentYear + 2; year++) {
        years.push(year.toString());
        years.push(year.toString() + '-' + (year + 1).toString());
      }
      return years;
    }
  },

  async mounted() {
    const statusData = getStatusData();
    if (statusData) {
      this.instructorName = statusData.student_name || '';
      this.isVivaAdmin = statusData.is_viva_admin || false;
    }
    const courseId = this.$route.params.id;

    if (courseId !== 'new') {
      this.isNewCourse = false;
      this.loading = true;

      try {
        const response = await apiClient.get(`/api/viva/instructor/course/${courseId}/`);

        if (response.data) {
          this.course = {
            id: response.data.id,
            title: response.data.title,
            term: response.data.term || '',
            year: response.data.year || ''
          };
          this.students = response.data.student_data || [];
          // Store co-teachers (excludes current user)
          this.coTeachers = response.data.co_teachers || [];
          // Store updated_at for concurrency
          this.originalUpdatedAt = response.data.updated_at || null;
        }
      } catch (error) {
        console.error('Failed to load course:', error);

        const errorMsg = handleApiError(error, this.$router, 'Failed to load course');
        if (error.response && error.response.status !== 401) {
          alert(errorMsg);
          this.$router.push({ name: 'instructor-dashboard' });
        }
      } finally {
        this.loading = false;
      }
    } else {
      this.loading = false;
    }
  },

  methods: {
    studentDisplayName(student) {
      const full = `${student.first_name || ''} ${student.last_name || ''}`.trim();
      return full || student.email;
    },

    async removeStudent(student) {
      if (!confirm(`Remove ${this.studentDisplayName(student)} from this course?`)) {
        return;
      }
      try {
        await apiClient.post(`/api/viva/instructor/course/${this.course.id}/remove-student/`, {
          student_email: student.email,
        });
        this.students = this.students.filter(s => s.email !== student.email);
      } catch (error) {
        console.error('Failed to remove student:', error);
        alert(error.response?.data?.error || 'Failed to remove student. Please try again.');
      }
    },

    formatName(name) {
      if (name.includes(', ')) {
        const [last, first] = name.split(', ');
        return `${first} ${last}`;
      }
      return name;
    },

    validate() {
      this.errors = {};
      let isValid = true;

      // Validate title
      if (!this.course.title || this.course.title.trim().length === 0) {
        this.errors.title = 'Course title is required';
        isValid = false;
      }

      return isValid;
    },

    async save() {
      if (!this.validate()) {
        return;
      }

      this.saving = true;

      try {
        const courseData = {
          title: this.course.title,
          term: this.course.term,
          year: this.course.year
        };

        if (this.isNewCourse) {
          // Create new course
          const response = await apiClient.post('/api/viva/instructor/course/', courseData);
          const newCourseId = response.data.course_id;

          if (newCourseId && this.coTeachers.length > 0) {
            // Add all pending co-teachers in parallel
            const results = await Promise.allSettled(
              this.coTeachers.map(teacher =>
                apiClient.post(`/api/viva/instructor/course/${newCourseId}/add-co-teacher/`, {
                  first_name: teacher.first_name || '',
                  last_name: teacher.last_name || '',
                  email: teacher.email,
                })
              )
            );

            const failed = results
              .map((result, i) => ({ result, teacher: this.coTeachers[i] }))
              .filter(({ result }) => result.status === 'rejected')
              .map(({ teacher }) => teacher.email);

            if (failed.length) {
              alert(`Course saved, but these co-teachers could not be added:\n${failed.join('\n')}`);
            }
          } else if (!newCourseId) {
            console.error('Course created but no ID returned:', response.data);
            if (this.coTeachers.length > 0) {
              alert(`Course saved, but co-teachers could not be added:\n${this.coTeachers.map(t => t.email).join('\n')}`);
            }
          }
        } else {
          // Include original_updated_at for concurrency check
          if (this.originalUpdatedAt) {
            courseData.original_updated_at = this.originalUpdatedAt;
          }
          // Update existing course
          await apiClient.put(`/api/viva/instructor/course/${this.course.id}/update/`, courseData);
        }

        // Navigate back on success
        this.goBack();

      } catch (error) {
        console.error('Failed to save course:', error);

        // Handle 409 conflict
        if (error.response && error.response.status === 409) {
          alert('This course was modified by another instructor. Please reload the page to refresh with the latest changes.');
          return;
        }

        const errorMsg = handleApiError(error, this.$router, 'Failed to save course');
        if (error.response && error.response.status !== 401) {
          this.errors.general = errorMsg;
          alert(errorMsg);
        }
      } finally {
        this.saving = false;
      }
    },

    async addCoTeacher() {
      this.coTeacherError = '';
      this.coTeacherSuccess = '';

      if (!this.newCoTeacher.first_name.trim()) {
        this.coTeacherError = 'First name is required';
        return;
      }
      if (!this.newCoTeacher.last_name.trim()) {
        this.coTeacherError = 'Last name is required';
        return;
      }
      if (!this.newCoTeacher.email.trim()) {
        this.coTeacherError = 'Email is required';
        return;
      }

      this.addingCoTeacher = true;

      if (this.isNewCourse) {
        const trimmedEmail = this.newCoTeacher.email.trim();
        const trimmedFirst = this.newCoTeacher.first_name.trim();
        const trimmedLast = this.newCoTeacher.last_name.trim();

        if (this.coTeachers.some(t => t.email === trimmedEmail)) {
          this.coTeacherError = 'This person has already been added.';
          this.addingCoTeacher = false;
          return;
        }

        // Course doesn't exist yet — store locally and add to API after save
        this.coTeachers.push({
          name: `${trimmedLast}, ${trimmedFirst}`,
          email: trimmedEmail,
          first_name: trimmedFirst,
          last_name: trimmedLast,
        });
        this.coTeacherSuccess = `${trimmedFirst} ${trimmedLast} will be added when you save.`;
        this.newCoTeacher = { first_name: '', last_name: '', email: '' };
        this.addingCoTeacher = false;
        setTimeout(() => { this.closeCoTeacherModal(); }, 1500);
        return;
      }

      try {
        const response = await apiClient.post(`/api/viva/instructor/course/${this.course.id}/add-co-teacher/`, {
          first_name: this.newCoTeacher.first_name.trim(),
          last_name: this.newCoTeacher.last_name.trim(),
          email: this.newCoTeacher.email.trim(),
        });

        // Add to local list
        this.coTeachers.push(response.data);
        this.coTeacherSuccess = `${response.data.name || response.data.email} has been added as a co-teacher.`;

        // Reset form
        this.newCoTeacher = { first_name: '', last_name: '', email: '' };

        // Close modal after brief delay
        setTimeout(() => {
          this.closeCoTeacherModal();
        }, 1500);

      } catch (error) {
        console.error('Failed to add co-teacher:', error);
        this.coTeacherError = error.response?.data?.error || 'Failed to add co-teacher. Please try again.';
        this.addingCoTeacher = false;
      }
    },

    closeCoTeacherModal() {
      this.showCoTeacherModal = false;
      this.coTeacherError = '';
      this.coTeacherSuccess = '';
      this.addingCoTeacher = false;
      this.newCoTeacher = { first_name: '', last_name: '', email: '' };
    },

    cancel() {
      this.goBack();
    },

    goBack() {
      if (this.$route.query.return_to === 'assignments' && this.course.id) {
        // Navigate back to assignments page
        this.$router.push({
          name: 'course-assignments',
          params: { id: this.course.id }
        });
      } else {
        // Navigate back to dashboard
        this.$router.push({ name: 'instructor-dashboard' });
      }
    }
  }
};
</script>

<style scoped>
.instructor-info {
  margin-bottom: 0;
  padding-bottom: 0;
  padding-top: 5px !important;
}

.viva-form-container {
  max-width: 1200px;
  margin: 0;
  margin-left: 30px;
  padding-top: 15px;
}

.form-select {
  min-width: 300px;
}

/* Student Roster Section */
.student-roster {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-top: 14px;
}

.student-roster-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 13px;
}

.student-roster-name {
  font-weight: 600;
  color: #333;
}

.student-roster-email {
  color: var(--viva-grey);
  font-size: 12px;
}

/* Co-Teacher Section */
.co-teacher-tags {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  padding-top: 14px;
}

.co-teacher-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background-color: rgba(0, 0, 0, 0.06);
  padding: 5px 12px;
  border-radius: 14px;
  font-size: 13px;
}

.co-teacher-tag-name {
  font-weight: 600;
  color: #333;
}

.co-teacher-tag-email {
  color: var(--viva-grey);
  font-size: 12px;
}

.co-teacher-empty {
  color: var(--viva-grey);
  font-size: 14px;
  padding-top: 4px;
}

/* Co-Teacher Modal Messages */
.co-teacher-error {
  background-color: #f8d7da;
  color: #721c24;
  padding: 10px 15px;
  border-radius: 6px;
  margin-bottom: 15px;
  font-size: 14px;
}

.co-teacher-success {
  background-color: #d4edda;
  color: #155724;
  padding: 10px 15px;
  border-radius: 6px;
  margin-bottom: 15px;
  font-size: 14px;
}
</style>
